import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { batch } from 'react-redux';
import styles from './SignIn.module.scss';
import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import GoogleButton from 'components/BookingForm/SocialButton/GoogleButton';
import FacebookButton from 'components/BookingForm/SocialButton/FacebookButton';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';
import { loginSocialThunk, loginThunk, updateSysType } from 'redux/authSlice';
import { UserCredential } from 'firebase/auth';
import { LOADING_STATUS } from 'utils/constants/common';
import { resendEmailVerificationLink } from 'services/APIs';
import { STEPS, popFamilyStep, pushFamilyStep } from 'redux/familyBookingSlice';
import {
  SOCIAL_LOGIN_ERROR_MESSAGE,
  checkSocialEmail,
  getSocialUserData,
} from '../../utils/socialLogin';

import FormTextInput from 'components/BookingForm/FormTextInput/FormTextInput';
import DentalButton from 'components/Buttons/DentalButton/DentalButton';
import { HelperText } from 'components/Form/HelperText/HelperText';
import { Link } from 'react-router-dom';
import { ROUTES } from 'routes/config';
import { renderToast } from 'components/CustomToast/CustomToast';

const SignIn = () => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    authSlice: { status },
  } = useAppSelector((state) => state);
  const [isEmailNotVerified, setIsEmailNotVerified] = useState(false);
  const [isResendDisabled, setIsResendDisabled] = useState(false);

  const dispatch = useAppDispatch();

  const { control, formState, getValues } = useForm({
    mode: 'all',
  });

  const handleResendVerifiedEmail = async () => {
    const isResendSuccessfully = await resendEmailVerificationLink(
      getValues('email')
    );

    renderToast({
      message: isResendSuccessfully
        ? 'Email has been sent successfully'
        : 'Something went wrong. Please try again later',
      type: isResendSuccessfully ? 'success' : 'error',
    });

    setIsResendDisabled(true);
  };

  const displayExtraErrorMessage = () => {
    if (!isEmailNotVerified) return;
    return (
      <div className={styles['error-container']}>
        <HelperText error={'Unverified email.'} />
        <button
          type="button"
          className={styles['resend-button']}
          onClick={handleResendVerifiedEmail}
          disabled={isResendDisabled}
        >
          Resend verification email
        </button>
      </div>
    );
  };

  const onLogin = async (e: any) => {
    e.preventDefault();

    const email = getValues('email');
    const password = getValues('password');

    const data = await dispatch(loginThunk({ email, password }));

    if (data.type === 'authSlice/login/fulfilled') {
      return;
    }

    if (data.payload === 'Unverified') return setIsEmailNotVerified(true);
    renderToast({ message: data.payload as string });
  };

  const handleSocialLogin = async (
    userCredential: UserCredential,
    sysType: string
  ) => {
    try {
      setIsLoading(true);

      const { email, userToken, isExistingEmail } = await checkSocialEmail(
        userCredential,
        sysType
      );

      if (isExistingEmail) {
        const result = await dispatch(
          loginSocialThunk({ email, sysType, userToken })
        );

        if (loginSocialThunk.rejected.match(result)) {
          renderToast({ message: result.payload as string });
        }

        return;
      }

      const socialData = await getSocialUserData(
        userCredential,
        sysType,
        userToken
      );

      batch(() => {
        dispatch(updateSysType({ socialData, sysType }));
        dispatch(pushFamilyStep(STEPS.SIGN_UP));
      });
    } catch (error) {
      renderToast({ message: SOCIAL_LOGIN_ERROR_MESSAGE });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form className={styles['container']} noValidate onSubmit={onLogin}>
      {isLoading && <LoadingScreen />}
      <div className={styles['otp-form']}>
        <div className={styles['section']}>
          <div>Sign in via social media</div>
          <div className={styles['social-section']}>
            <GoogleButton onSuccess={handleSocialLogin} />
            <FacebookButton onSuccess={handleSocialLogin} />
          </div>
          <div className={styles['fill-section']}>or by email</div>
        </div>
        <div className={styles['form-section']}>
          <FormTextInput
            autoComplete="email"
            type="text"
            name="email"
            label="Email address"
            control={control}
            placeholder="Input your email"
            required="This field is required"
            extraErrorMessage={displayExtraErrorMessage()}
            rules={{
              onChange: (event: any) => {
                const value = event.target.value;
                if (value && isEmailNotVerified) {
                  setIsEmailNotVerified(false);
                  setIsResendDisabled(false);
                }
              },
            }}
          />
          <FormTextInput
            type="password"
            name="password"
            label="Password"
            control={control}
            placeholder="Input your password"
            required="This field is required"
          />
          <Link
            to={ROUTES.FORGOT_PASSWORD_PAGE}
            className={styles.link}
            target="_blank"
          >
            Forgot password?
          </Link>
        </div>
        <div className={styles['footer-section']}>
          <DentalButton
            variant="outlined"
            onClick={() => dispatch(popFamilyStep())}
          >
            Back
          </DentalButton>
          <DentalButton
            disabled={!formState.isValid || isEmailNotVerified}
            variant="contained"
            isLoading={status === LOADING_STATUS.LOADING}
            onClick={onLogin}
            type="submit"
          >
            Sign in
          </DentalButton>
        </div>
      </div>
    </form>
  );
};

export default SignIn;
